import { useState, useEffect, useRef } from "react";

export default function MemoryGame({ onComplete, color, dark, tier = 'builder' }: any) {
  const STARTER_PAIRS = [
    { emoji:'💰', label:'Money',   mEmoji:'🔄', mLabel:'Trade Tool'       },
    { emoji:'🐷', label:'Save',    mEmoji:'⏳', mLabel:'Keep for Later'   },
    { emoji:'🧾', label:'Budget',  mEmoji:'📐', mLabel:'Spending Plan'    },
    { emoji:'💵', label:'Income',  mEmoji:'💼', mLabel:'Money Earned'     },
    { emoji:'💸', label:'Expense', mEmoji:'🛒', mLabel:'Money Spent'      },
    { emoji:'🎯', label:'Goal',    mEmoji:'🏆', mLabel:'Save Target'      },
  ];
  const BUILDER_PAIRS = [
    { emoji:'📈', label:'Stock',    mEmoji:'🏢', mLabel:'Own a Piece'      },
    { emoji:'💳', label:'Credit',   mEmoji:'📋', mLabel:'Borrow Now'       },
    { emoji:'📊', label:'Interest', mEmoji:'💸', mLabel:'Cost of Debt'     },
    { emoji:'🧺', label:'Index',    mEmoji:'📊', mLabel:'Market Basket'    },
    { emoji:'🔁', label:'Compound', mEmoji:'📈', mLabel:'Growth on Growth' },
    { emoji:'⚠️', label:'Debt',    mEmoji:'💸', mLabel:'Money Owed'       },
  ];
  const MASTER_PAIRS = [
    { emoji:'🏛️', label:'Bond',      mEmoji:'🤝', mLabel:'Lend to Gov'     },
    { emoji:'⚖️', label:'Diversify', mEmoji:'🧺', mLabel:'Spread Risk'     },
    { emoji:'🔥', label:'Inflation', mEmoji:'📦', mLabel:'Prices Rise'     },
    { emoji:'🦅', label:'Roth IRA',  mEmoji:'🌅', mLabel:'Tax-Free Growth' },
    { emoji:'💧', label:'Liquidity', mEmoji:'⚡', mLabel:'Easy to Cash'    },
    { emoji:'📈', label:'Cap Gain',  mEmoji:'💰', mLabel:'Profit on Sale'  },
  ];

  const ALL    = tier==='starter'?STARTER_PAIRS:tier==='master'?MASTER_PAIRS:BUILDER_PAIRS;
  const NPAIRS = 4;
  const TIME   = 50;

  const [cards] = useState(() => {
    const chosen = [...ALL].sort(() => Math.random()-0.5).slice(0, NPAIRS);
    return [
      ...chosen.map((p,i) => ({ id:i*2,   pair:i, emoji:p.emoji,  label:p.label  })),
      ...chosen.map((p,i) => ({ id:i*2+1, pair:i, emoji:p.mEmoji, label:p.mLabel })),
    ].sort(() => Math.random()-0.5);
  });

  const [flipped,  setFlipped]  = useState<number[]>([]);
  const [matched,  setMatched]  = useState<number[]>([]);
  const [locked,   setLocked]   = useState(false);
  const [moves,    setMoves]    = useState(0);
  const [timeLeft, setTimeLeft] = useState(TIME);
  const [shaking,  setShaking]  = useState<number[]>([]);
  const movesRef = useRef(0);
  const done = matched.length === NPAIRS;

  useEffect(() => {
    if (done) return;
    const t = setInterval(() => setTimeLeft(s => Math.max(0, s-1)), 1000);
    return () => clearInterval(t);
  }, [done]);

  useEffect(() => {
    if (done) setTimeout(() => onComplete(movesRef.current), 900);
  }, [matched]);

  function flip(i: number) {
    if (locked || flipped.includes(i) || matched.includes(cards[i].pair) || timeLeft === 0) return;
    const next = [...flipped, i];
    setFlipped(next);
    if (next.length === 2) {
      setLocked(true);
      movesRef.current++; setMoves(movesRef.current);
      const [a, b] = [cards[next[0]], cards[next[1]]];
      if (a.pair === b.pair) {
        setTimeout(() => { setMatched(m => [...m, a.pair]); setFlipped([]); setLocked(false); }, 500);
      } else {
        setShaking(next);
        setTimeout(() => { setFlipped([]); setLocked(false); setShaking([]); }, 900);
      }
    }
  }

  const timerPct = (timeLeft / TIME) * 100;
  const timerClr = timeLeft < 10 ? '#ef4444' : timeLeft < 20 ? '#f59e0b' : color;
  const stars    = done ? (timeLeft > 30 ? 3 : timeLeft > 15 ? 2 : 1) : 0;

  return (
    <div style={{ fontFamily:'Nunito' }}>
      {/* HUD */}
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:6 }}>
        <span style={{ fontSize:13, fontWeight:900, color }}>✅ {matched.length}/{NPAIRS}</span>
        <span style={{ fontSize:13, fontWeight:900, color:'#374151' }}>🔄 {moves} tries</span>
        <span style={{ fontSize:13, fontWeight:900, color:timerClr,
          animation:timeLeft<10?'timerWarn 0.8s infinite':'none' }}>⏱ {timeLeft}s</span>
      </div>

      {/* Timer bar */}
      <div style={{ height:7, background:'#e5e7eb', borderRadius:99, marginBottom:12, overflow:'hidden' }}>
        <div style={{ height:'100%', width:`${timerPct}%`, background:timerClr,
          borderRadius:99, transition:'width 1s linear, background 0.3s' }} />
      </div>

      {/* 4-column card grid */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:7 }}>
        {cards.map((c, i) => {
          const show    = flipped.includes(i) || matched.includes(c.pair);
          const isMatch = matched.includes(c.pair);
          const isShk   = shaking.includes(i);
          return (
            <div key={c.id} className="flip-card"
              style={{ height:76, animation:isShk?'shake 0.45s':'none',
                cursor:(!isMatch && !locked && timeLeft>0)?'pointer':'default' }}
              onClick={() => flip(i)}>
              <div className={`flip-inner${show?' is-flipped':''}`}>
                {/* Face-down: gradient with ? */}
                <div className="flip-front"
                  style={{ background:`linear-gradient(135deg,${color},${dark})`,
                    boxShadow:`0 3px 10px ${color}55` }}>
                  <div style={{ fontSize:24, filter:'drop-shadow(0 2px 4px rgba(0,0,0,0.25))' }}>❓</div>
                </div>
                {/* Face-up: emoji + label */}
                <div className="flip-back"
                  style={{ background:isMatch?`linear-gradient(135deg,${color}28,${color}48)`:'linear-gradient(135deg,#fffbf5,#fff8f0)',
                    border:`2.5px solid ${isMatch?color:dark+'80'}`,
                    boxShadow:isMatch?`0 0 14px ${color}66,inset 0 0 6px ${color}22`:'0 2px 6px rgba(0,0,0,0.08)',
                    animation:isMatch?'matchBurst 0.5s':'none' }}>
                  <div style={{ fontSize:18 }}>{c.emoji}</div>
                  <div style={{ fontSize:9, fontWeight:900, color:isMatch?color:dark,
                    textAlign:'center', lineHeight:1.2, padding:'0 3px' }}>{c.label}</div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Completion */}
      {done && (
        <div style={{ textAlign:'center', marginTop:14, animation:'pop 0.4s' }}>
          <div style={{ fontSize:32 }}>{'⭐'.repeat(stars)}{'🌑'.repeat(3-stars)}</div>
          <div style={{ fontSize:18, fontWeight:900, color, marginTop:6 }}>
            {stars===3?'Lightning fast! 🔥':stars===2?'Nice work! 💪':'Good memory! 🧠'}
          </div>
          <div style={{ fontSize:12, color:'#6b7280', fontWeight:700, marginTop:4 }}>
            {NPAIRS} pairs matched in {moves} tries
          </div>
        </div>
      )}
      {timeLeft===0 && !done && (
        <div style={{ textAlign:'center', marginTop:14, animation:'pop 0.4s' }}>
          <div style={{ fontSize:32 }}>⏰</div>
          <div style={{ fontSize:16, fontWeight:900, color:'#ef4444', marginTop:6 }}>
            Time's up! Give it another go!
          </div>
        </div>
      )}
    </div>
  );
}
